import { useMemo, useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { ConfirmDialog } from 'components/common'
import {
  AccountPayableRecordStatus,
  AccountsPayableRecordCategory,
  useCreatePaymentMutation,
  useGetAccountPayableDriverNameQuery,
  useGetMissingDriverPaymentDataFieldsQuery,
  useMarkAsPaidExternallyMutation,
  useUpdateAccountPayableRecordCategoryMutation,
  useUpdateAccountPayableRecordStatusMutation,
} from 'generated/graphql'
import { Restricted } from 'providers'

const paymentCategoryOptions: Array<{ value: AccountsPayableRecordCategory; label: string }> = [
  { value: AccountsPayableRecordCategory.Telleroo, label: 'Telleroo' },
  { value: AccountsPayableRecordCategory.HedgeFlows, label: 'HedgeFlows' },
  { value: AccountsPayableRecordCategory.SmartPay, label: 'SmartPay' },
]

function DriverPanel() {
  const history = useHistory()
  const { id } = useParams<{ id?: string }>()
  const [confirmState, setConfirmState] = useState({
    approve: false,
    flag: false,
    payedExternally: false,
    changeCategory: false,
  })
  const [selectedCategory, setSelectedCategory] = useState<AccountsPayableRecordCategory | ''>('')

  // Queries
  const {
    data: accountPayableData,
    loading: apLoading,
    refetch,
  } = useGetAccountPayableDriverNameQuery({
    variables: { id: Number(id) },
    skip: !id,
    fetchPolicy: 'network-only',
  })
  const driverName =
    accountPayableData?.accountPayableRecord?.bookingInvoice.bookingDriver?.driver.primaryDriverName
  const bookingReference =
    accountPayableData?.accountPayableRecord?.bookingInvoice.booking.ourReference
  const batchId = accountPayableData?.accountPayableRecord?.bookingInvoiceBatchLine?.batchId
  const statusLabel = accountPayableData?.accountPayableRecord?.statusLabel
  const currentCategory = accountPayableData?.accountPayableRecord?.category ?? ''

  const { data: missingPaymentData } = useGetMissingDriverPaymentDataFieldsQuery({
    variables: {
      accountPayableRecordId: Number(id),
    },
    skip: !Number(id),
    fetchPolicy: 'network-only',
  })

  const missingPaymentDataFields = missingPaymentData?.missingDriverPaymentDataFields || []
  const hasMissingPaymentDataFields = missingPaymentDataFields.length > 0
  const changeCategoryDisabled = !selectedCategory || selectedCategory === currentCategory

  // Mutations
  const [createPayment, { loading: creatingPayment }] = useCreatePaymentMutation({
    onCompleted: () => {
      refetch({ id: Number(id) })
      toast.success('Payment approved')
    },
    onError: () => {
      toast.error('Error approving payment')
    },
  })

  const [updateAccountPayableRecordStatus] = useUpdateAccountPayableRecordStatusMutation({
    onCompleted: () => {
      refetch({ id: Number(id) })
    },
  })

  const [markAsPaidExternally] = useMarkAsPaidExternallyMutation()

  const [updateAccountPayableRecordCategory, { loading: updatingCategory }] =
    useUpdateAccountPayableRecordCategoryMutation({
      onCompleted: () => {
        refetch({ id: Number(id) })
        toast.success('Payment category updated')
      },
    })

  const [disableFlag, disableApprove, disableChangeCategory, smartPayTooltip] = useMemo(() => {
    if (accountPayableData?.accountPayableRecord?.status) {
      const isUnderReview =
        accountPayableData.accountPayableRecord.status === AccountPayableRecordStatus.UnderReview
      const isFlagged =
        accountPayableData.accountPayableRecord.status === AccountPayableRecordStatus.Flagged
      const isSmartPay =
        accountPayableData.accountPayableRecord.category === AccountsPayableRecordCategory.SmartPay

      const tooltip = isSmartPay
        ? 'SmartPay records cannot be approved through this process. Please use the SmartPay approval workflow.'
        : ''

      return [
        !isUnderReview || isSmartPay,
        (!isUnderReview && !isFlagged) || isSmartPay,
        !isUnderReview && !isFlagged,
        tooltip,
      ]
    }
    return [true, true, true, '']
  }, [
    accountPayableData?.accountPayableRecord?.status,
    accountPayableData?.accountPayableRecord?.category,
  ])

  // handlers
  const handleApprove = () => {
    if (id) {
      createPayment({
        variables: {
          accountPayableId: id,
        },
      })
    }
  }

  const handleFlag = () => {
    updateAccountPayableRecordStatus({
      variables: {
        input: {
          id: Number(id),
          status: AccountPayableRecordStatus.Flagged,
        },
      },
    })
  }

  const handleMarkAsPayedExternally = () => {
    markAsPaidExternally({
      variables: {
        id: Number(id),
      },
    })
  }

  const handleChangeCategoryClick = () => {
    setSelectedCategory(currentCategory)
    setConfirmState((prevState) => ({
      ...prevState,
      changeCategory: true,
    }))
  }

  const handleChangeCategoryConfirm = () => {
    if (!id || changeCategoryDisabled) {
      return
    }

    updateAccountPayableRecordCategory({
      variables: {
        input: {
          id: Number(id),
          category: selectedCategory as AccountsPayableRecordCategory,
        },
      },
    })
  }

  const handleCloseClick = () => {
    const previousUrl = localStorage.getItem('previousUrl')
    if (previousUrl) {
      const url = new URL(previousUrl)
      history.replace(url.pathname + url.hash)
    }
    localStorage.removeItem('previousUrl')
  }

  const handlePayedExternallyClick = () =>
    setConfirmState((prevState) => ({
      ...prevState,
      payedExternally: true,
    }))

  const handleFlagClick = () =>
    setConfirmState((prevState) => ({
      ...prevState,
      flag: true,
    }))

  const handleApproveAllClick = () =>
    setConfirmState((prevState) => ({
      ...prevState,
      approve: true,
    }))

  return (
    <Paper elevation={4}>
      <ConfirmDialog
        open={confirmState.approve}
        onConfirm={handleApprove}
        setOpen={(open) => setConfirmState((prevState) => ({ ...prevState, approve: open }))}
        title='Approve Account Payable Record'
        message={`You chose to approve the selected Record: ${accountPayableData?.accountPayableRecord?.bookingInvoice.booking.ourReference}`}
        option1='Cancel'
        option2='Approve'
      />
      <ConfirmDialog
        open={confirmState.flag}
        onConfirm={handleFlag}
        setOpen={(open) => setConfirmState((prevState) => ({ ...prevState, flag: open }))}
        title='Flag Account Payable Record'
        message={`You chose to flag the selected Record: ${accountPayableData?.accountPayableRecord?.bookingInvoice.booking.ourReference}`}
        option1='Cancel'
        option2='Confirm'
      />
      <ConfirmDialog
        open={confirmState.payedExternally}
        onConfirm={handleMarkAsPayedExternally}
        setOpen={(open) =>
          setConfirmState((prevState) => ({ ...prevState, payedExternally: open }))
        }
        title='Mark as Payed Externally'
        message={`When you mark an Account Payable Record as 'Paid Externally', it will be archived. After this action, no further actions can be performed on the Record. Use this option when a Booking has been allocated to a Network Partner, and the payment was made externally. Do you wish to proceed?`}
        option1='Cancel'
        option2='Ok'
      />
      <ConfirmDialog
        open={confirmState.changeCategory}
        onConfirm={handleChangeCategoryConfirm}
        setOpen={(open) => setConfirmState((prevState) => ({ ...prevState, changeCategory: open }))}
        onCancel={() => setSelectedCategory('')}
        title='Change Payment Category'
        option1='Cancel'
        option2='Update'
        tooltip2={changeCategoryDisabled ? 'Select a different category' : ''}
        disableConfirmButton={changeCategoryDisabled}>
        <Stack spacing={2}>
          <Typography variant='body2'>
            Choose a payment category that best reflects how this record should be processed.
          </Typography>
          <FormControl fullWidth variant='outlined'>
            <InputLabel id='payment-category-label'>Category</InputLabel>
            <Select
              labelId='payment-category-label'
              id='payment-category-select'
              label='Category'
              value={selectedCategory}
              onChange={(event) =>
                setSelectedCategory(event.target.value as AccountsPayableRecordCategory)
              }>
              {paymentCategoryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </ConfirmDialog>
      <Stack direction='row' p={2} alignItems='center' justifyContent='space-between'>
        <Stack>
          <Typography variant='h6'>{driverName}</Typography>
          <Typography>
            {bookingReference}
            {batchId && (
              <>
                {' — Batch ID: '}
                {batchId}
              </>
            )}
          </Typography>
          <Typography color={(theme) => theme.palette.error.main}>{statusLabel}</Typography>
        </Stack>
        <Stack direction='row' alignSelf='stretch' spacing={2}>
          <Stack direction='row' alignSelf='center' spacing={2}>
            <Restricted to='ViewAccountsPayableRecordDocumentActions'>
              <LoadingButton
                disabled={disableApprove}
                variant='outlined'
                color='primary'
                onClick={handlePayedExternallyClick}>
                Mark as Payed Externally
              </LoadingButton>
              <LoadingButton
                loading={updatingCategory}
                disabled={disableChangeCategory}
                variant='outlined'
                color='primary'
                onClick={handleChangeCategoryClick}>
                Change category
              </LoadingButton>
              <LoadingButton
                disabled={disableFlag}
                loading={creatingPayment || apLoading}
                variant='outlined'
                color='secondary'
                onClick={handleFlagClick}>
                Flag
              </LoadingButton>
              <Tooltip title={smartPayTooltip} arrow>
                <span>
                  <LoadingButton
                    disabled={disableApprove || hasMissingPaymentDataFields}
                    loading={creatingPayment || apLoading}
                    variant='outlined'
                    onClick={handleApproveAllClick}>
                    Approve
                  </LoadingButton>
                </span>
              </Tooltip>
            </Restricted>
          </Stack>
          <Box>
            <IconButton onClick={handleCloseClick}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  )
}

export default DriverPanel
